<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
      <div id="layout-wrapper">
        <NavigationBar />

        <div id="content" class="content-minimize">
          <div class="page-content">
            <div class="container-fluid">

              <!-- Page Title -->
              <div class="row">
                <div class="col-12">
                  <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 class="mb-sm-0 font-size-18">Drug Catalog</h4>
                    <div class="page-title-right">
                      <ol class="breadcrumb m-0">
                        <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                        <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('pharmacy-dashboard')">Pharmacy</a></li>
                        <li class="breadcrumb-item active">Drug Catalog</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Main Card -->
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-body">
                      <!-- Header Actions -->
                      <div class="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h5 class="mb-1"><i class="mdi mdi-pill me-2 text-primary"></i>Drug Master List</h5>
                          <small class="text-muted">Manage all drugs in the system</small>
                        </div>
                        <div class="d-flex gap-2">
                          <button class="btn btn-primary" @click="openAddDrugModal">
                            <i class="mdi mdi-plus me-1"></i> Add New Drug
                          </button>
                          <button class="btn btn-outline-secondary" @click="goTo('pharmacy-inventory')">
                            <i class="mdi mdi-package-variant me-1"></i> Inventory
                          </button>
                        </div>
                      </div>

                      <!-- Search & Filter -->
                      <div class="row mb-3">
                        <div class="col-md-4">
                          <div class="input-group">
                            <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                            <input type="text" class="form-control" v-model="searchTerm" @input="debounceSearch" placeholder="Search drug name, code...">
                            <button v-if="searchTerm" class="btn btn-light" @click="clearSearch"><i class="mdi mdi-close"></i></button>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <select class="form-select" v-model="filterCategory" @change="loadDrugs">
                            <option value="">All Categories</option>
                            <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                          </select>
                        </div>
                        <div class="col-md-3">
                          <select class="form-select" v-model="filterForm" @change="loadDrugs">
                            <option value="">All Forms</option>
                            <option v-for="form in drugForms" :key="form" :value="form">{{ form }}</option>
                          </select>
                        </div>
                        <div class="col-md-2">
                          <button class="btn btn-outline-secondary w-100" @click="loadDrugs">
                            <i class="mdi mdi-refresh me-1"></i> Refresh
                          </button>
                        </div>
                      </div>

                      <!-- Loading -->
                      <div v-if="loading" class="text-center py-5">
                        <div class="spinner-border text-primary"></div>
                        <p class="text-muted mt-2 mb-0">Loading drugs...</p>
                      </div>

                      <!-- Drugs Table -->
                      <div v-else class="table-responsive">
                        <table class="table table-hover table-striped align-middle">
                          <thead class="table-light">
                            <tr>
                              <th>Drug Code</th>
                              <th>Drug Name</th>
                              <th>Category</th>
                              <th>Form / Strength</th>
                              <th class="text-end">Price (KES)</th>
                              <th class="text-center">Status</th>
                              <th class="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="drug in drugs" :key="drug.drugID">
                              <td><code>{{ drug.drugCode }}</code></td>
                              <td>
                                <div class="fw-medium">{{ drug.genericName }}</div>
                                <small class="text-muted">{{ drug.brandName }}</small>
                                <span v-if="drug.isEssential === 'Yes'" class="badge bg-success-subtle text-success ms-1">Essential</span>
                                <span v-if="drug.isControlled === 'Yes'" class="badge bg-danger-subtle text-danger ms-1">Controlled</span>
                              </td>
                              <td><span class="badge bg-light text-dark">{{ drug.category }}</span></td>
                              <td>
                                {{ drug.drugForm }}
                                <small class="text-muted d-block">{{ drug.strength }} {{ drug.unitOfMeasure }}</small>
                              </td>
                              <td class="text-end">{{ numberFormat(drug.standardPrice) }}</td>
                              <td class="text-center">
                                <span class="badge" :class="drug.status === 'Active' ? 'bg-success' : 'bg-secondary'">
                                  {{ drug.status }}
                                </span>
                              </td>
                              <td class="text-center">
                                <div class="btn-group btn-group-sm">
                                  <button class="btn btn-soft-primary" @click="viewDrug(drug)" title="View">
                                    <i class="mdi mdi-eye"></i>
                                  </button>
                                  <button class="btn btn-soft-warning" @click="editDrug(drug)" title="Edit">
                                    <i class="mdi mdi-pencil"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            <tr v-if="drugs.length === 0">
                              <td colspan="7" class="text-center py-4 text-muted">
                                <i class="mdi mdi-pill-off font-size-24 d-block mb-2"></i>
                                No drugs found
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Pagination -->
                      <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">
                          Showing {{ ((currentPage - 1) * pageSize) + 1 }} - {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }}
                        </small>
                        <nav>
                          <ul class="pagination pagination-sm mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" href="javascript:void(0);" @click="goToPage(currentPage - 1)">Prev</a>
                            </li>
                            <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                              <a class="page-link" href="javascript:void(0);" @click="goToPage(page)">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                              <a class="page-link" href="javascript:void(0);" @click="goToPage(currentPage + 1)">Next</a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <footer class="footer">
            <div class="container-fluid">
              <div class="row">
                <div class="col-sm-6"></div>
                <div class="col-sm-6">
                  <div class="text-sm-end d-sm-block">County Hospital Management System</div>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>

      <!-- Add/Edit Drug Modal -->
      <div class="modal fade" id="drugModal" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="mdi me-2" :class="isEditing ? 'mdi-pencil text-warning' : 'mdi-plus text-primary'"></i>
                {{ isEditing ? 'Edit Drug' : 'Add New Drug' }}
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="saveDrug">
                <div class="row">
                  <!-- Drug Code -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Drug Code <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="drugForm.drugCode" required placeholder="e.g., PARA500">
                  </div>

                  <!-- Generic Name -->
                  <div class="col-md-8 mb-3">
                    <label class="form-label">Generic Name <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="drugForm.genericName" required placeholder="e.g., Paracetamol">
                  </div>

                  <!-- Brand Name -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Brand Name</label>
                    <input type="text" class="form-control" v-model="drugForm.brandName" placeholder="e.g., Panadol">
                  </div>

                  <!-- Category -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Category <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="drugForm.category" required>
                      <option value="">Select Category</option>
                      <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                    </select>
                  </div>

                  <!-- Drug Form -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Drug Form <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="drugForm.drugForm" required>
                      <option value="">Select Form</option>
                      <option v-for="form in drugForms" :key="form" :value="form">{{ form }}</option>
                    </select>
                  </div>

                  <!-- Strength -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Strength <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="drugForm.strength" required placeholder="e.g., 500mg">
                  </div>

                  <!-- Unit of Measure -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Unit of Measure</label>
                    <select class="form-select" v-model="drugForm.unitOfMeasure">
                      <option value="">Select Unit</option>
                      <option value="mg">mg</option>
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                      <option value="mcg">mcg</option>
                      <option value="IU">IU</option>
                      <option value="%">%</option>
                    </select>
                  </div>

                  <!-- Pack Size -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Pack Size</label>
                    <input type="text" class="form-control" v-model="drugForm.packSize" placeholder="e.g., 10 tablets">
                  </div>

                  <!-- Route of Administration -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Route</label>
                    <select class="form-select" v-model="drugForm.routeOfAdministration">
                      <option value="">Select Route</option>
                      <option v-for="route in routes" :key="route.code" :value="route.code">{{ route.name }}</option>
                    </select>
                  </div>

                  <!-- Standard Price -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Standard Price (KES) <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="drugForm.standardPrice" required min="0" step="0.01" placeholder="0.00">
                  </div>

                  <!-- Reorder Level -->
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Reorder Level</label>
                    <input type="number" class="form-control" v-model="drugForm.reorderLevel" min="0" placeholder="Minimum stock level">
                  </div>

                  <!-- Standard Dosage -->
                  <div class="col-md-8 mb-3">
                    <label class="form-label">Standard Dosage</label>
                    <input type="text" class="form-control" v-model="drugForm.standardDosage" placeholder="e.g., 1 tablet 3 times daily">
                  </div>

                  <!-- Flags -->
                  <div class="col-12 mb-3">
                    <div class="d-flex gap-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="drugForm.isEssential" id="isEssential" true-value="Yes" false-value="No">
                        <label class="form-check-label" for="isEssential">
                          <i class="mdi mdi-star text-success me-1"></i> Essential Drug
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="drugForm.isControlled" id="isControlled" true-value="Yes" false-value="No">
                        <label class="form-check-label" for="isControlled">
                          <i class="mdi mdi-shield-alert text-danger me-1"></i> Controlled Substance
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Description -->
                  <div class="col-12 mb-3">
                    <label class="form-label">Description / Notes</label>
                    <textarea class="form-control" rows="2" v-model="drugForm.description" placeholder="Additional information about the drug..."></textarea>
                  </div>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="saveDrug" :disabled="!canSaveDrug || saving">
                <span v-if="saving"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
                <span v-else><i class="mdi mdi-check me-1"></i> {{ isEditing ? 'Update Drug' : 'Add Drug' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- View Drug Modal -->
      <div class="modal fade" id="viewDrugModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title"><i class="mdi mdi-information me-2 text-info"></i>Drug Details</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body" v-if="selectedDrug">
              <div class="text-center mb-4">
                <div class="avatar-lg mx-auto mb-3">
                  <span class="avatar-title rounded-circle bg-primary-subtle text-primary font-size-24">
                    <i class="mdi mdi-pill"></i>
                  </span>
                </div>
                <h5 class="mb-1">{{ selectedDrug.genericName }}</h5>
                <p class="text-muted mb-0">{{ selectedDrug.brandName }}</p>
                <code>{{ selectedDrug.drugCode }}</code>
              </div>

              <table class="table table-borderless table-sm">
                <tr>
                  <th width="40%">Category:</th>
                  <td>{{ selectedDrug.category }}</td>
                </tr>
                <tr>
                  <th>Form:</th>
                  <td>{{ selectedDrug.drugForm }}</td>
                </tr>
                <tr>
                  <th>Strength:</th>
                  <td>{{ selectedDrug.strength }} {{ selectedDrug.unitOfMeasure }}</td>
                </tr>
                <tr>
                  <th>Pack Size:</th>
                  <td>{{ selectedDrug.packSize || '-' }}</td>
                </tr>
                <tr>
                  <th>Route:</th>
                  <td>{{ selectedDrug.routeOfAdministration || '-' }}</td>
                </tr>
                <tr>
                  <th>Standard Price:</th>
                  <td><strong>KES {{ numberFormat(selectedDrug.standardPrice) }}</strong></td>
                </tr>
                <tr>
                  <th>Reorder Level:</th>
                  <td>{{ selectedDrug.reorderLevel || '-' }}</td>
                </tr>
                <tr>
                  <th>Standard Dosage:</th>
                  <td>{{ selectedDrug.standardDosage || '-' }}</td>
                </tr>
                <tr>
                  <th>Essential:</th>
                  <td>
                    <span class="badge" :class="selectedDrug.isEssential === 'Yes' ? 'bg-success' : 'bg-secondary'">
                      {{ selectedDrug.isEssential }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Controlled:</th>
                  <td>
                    <span class="badge" :class="selectedDrug.isControlled === 'Yes' ? 'bg-danger' : 'bg-secondary'">
                      {{ selectedDrug.isControlled }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Status:</th>
                  <td>
                    <span class="badge" :class="selectedDrug.status === 'Active' ? 'bg-success' : 'bg-secondary'">
                      {{ selectedDrug.status }}
                    </span>
                  </td>
                </tr>
                <tr v-if="selectedDrug.description">
                  <th>Description:</th>
                  <td>{{ selectedDrug.description }}</td>
                </tr>
              </table>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
              <button type="button" class="btn btn-warning" @click="editDrugFromView">
                <i class="mdi mdi-pencil me-1"></i> Edit
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast Notification -->
      <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "DrugCatalog",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: false,
      saving: false,

      // Drugs List
      drugs: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 50,

      // Search & Filter
      searchTerm: '',
      searchTimeout: null,
      filterCategory: '',
      filterForm: '',

      // Categories
      categories: [
        'Analgesics',
        'Antibiotics',
        'Antihistamines',
        'Antihypertensives',
        'Antidiabetics',
        'Antivirals',
        'Antifungals',
        'Anti-inflammatories',
        'Cardiovascular',
        'Dermatological',
        'Gastrointestinal',
        'Hormones',
        'Immunosuppressants',
        'Muscle Relaxants',
        'Neurological',
        'Ophthalmic',
        'Psychiatric',
        'Respiratory',
        'Supplements',
        'Vaccines',
        'Vitamins',
        'Other'
      ],

      // Drug Forms
      drugForms: [
        'Tablet',
        'Capsule',
        'Syrup',
        'Suspension',
        'Injection',
        'Cream',
        'Ointment',
        'Gel',
        'Drops',
        'Inhaler',
        'Suppository',
        'Patch',
        'Powder',
        'Solution',
        'Spray',
        'Lotion',
        'Other'
      ],

      // Routes of Administration
      routes: [
        { code: 'PO', name: 'Oral (PO)' },
        { code: 'IV', name: 'Intravenous (IV)' },
        { code: 'IM', name: 'Intramuscular (IM)' },
        { code: 'SC', name: 'Subcutaneous (SC)' },
        { code: 'SL', name: 'Sublingual (SL)' },
        { code: 'PR', name: 'Rectal (PR)' },
        { code: 'TOP', name: 'Topical' },
        { code: 'INH', name: 'Inhalation' },
        { code: 'OPH', name: 'Ophthalmic' },
        { code: 'OTC', name: 'Otic (Ear)' },
        { code: 'NAS', name: 'Nasal' },
        { code: 'VAG', name: 'Vaginal' }
      ],

      // Drug Form
      isEditing: false,
      drugForm: {
        drugID: '',
        drugCode: '',
        genericName: '',
        brandName: '',
        category: '',
        drugForm: '',
        strength: '',
        unitOfMeasure: '',
        packSize: '',
        routeOfAdministration: '',
        standardPrice: '',
        reorderLevel: '',
        standardDosage: '',
        isEssential: 'No',
        isControlled: 'No',
        description: ''
      },

      // Selected Drug for View
      selectedDrug: null,

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    canSaveDrug() {
      return this.drugForm.drugCode &&
        this.drugForm.genericName &&
        this.drugForm.category &&
        this.drugForm.drugForm &&
        this.drugForm.strength &&
        this.drugForm.standardPrice;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    numberFormat(num) {
      return Number(num || 0).toLocaleString();
    },

    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadDrugs();
      }, 300);
    },

    clearSearch() {
      this.searchTerm = '';
      this.loadDrugs();
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.loadDrugs();
    },

    // Load Drugs
    loadDrugs() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getDrugs");
      data.append("searchTerm", this.searchTerm);
      data.append("category", this.filterCategory);

      execute(data, hospital)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            this.drugs = res.data.data.drugs || [];
            this.totalCount = this.drugs.length;
          } else {
            this.drugs = [];
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error(e);
        });
    },

    // Open Add Drug Modal
    openAddDrugModal() {
      this.isEditing = false;
      this.drugForm = {
        drugID: '',
        drugCode: '',
        genericName: '',
        brandName: '',
        category: '',
        drugForm: '',
        strength: '',
        unitOfMeasure: '',
        packSize: '',
        routeOfAdministration: '',
        standardPrice: '',
        reorderLevel: '',
        standardDosage: '',
        isEssential: 'No',
        isControlled: 'No',
        description: ''
      };
      const modal = new bootstrap.Modal(document.getElementById('drugModal'));
      modal.show();
    },

    // Edit Drug
    editDrug(drug) {
      this.isEditing = true;
      this.drugForm = { ...drug };
      const modal = new bootstrap.Modal(document.getElementById('drugModal'));
      modal.show();
    },

    // Edit from View Modal
    editDrugFromView() {
      bootstrap.Modal.getInstance(document.getElementById('viewDrugModal')).hide();
      setTimeout(() => {
        this.editDrug(this.selectedDrug);
      }, 300);
    },

    // Save Drug
    saveDrug() {
      if (!this.canSaveDrug) return;

      this.saving = true;
      const data = new FormData();
      data.append("function", this.isEditing ? "updateDrug" : "addDrug");
      
      if (this.isEditing) {
        data.append("drugID", this.drugForm.drugID);
      }
      
      data.append("drugCode", this.drugForm.drugCode);
      data.append("genericName", this.drugForm.genericName);
      data.append("brandName", this.drugForm.brandName);
      data.append("category", this.drugForm.category);
      data.append("drugForm", this.drugForm.drugForm);
      data.append("strength", this.drugForm.strength);
      data.append("unitOfMeasure", this.drugForm.unitOfMeasure);
      data.append("packSize", this.drugForm.packSize);
      data.append("routeOfAdministration", this.drugForm.routeOfAdministration);
      data.append("standardPrice", this.drugForm.standardPrice);
      data.append("reorderLevel", this.drugForm.reorderLevel);
      data.append("standardDosage", this.drugForm.standardDosage);
      data.append("isEssential", this.drugForm.isEssential);
      data.append("isControlled", this.drugForm.isControlled);
      data.append("description", this.drugForm.description);
      data.append("createdBy", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
        .then((res) => {
          this.saving = false;
          if (res.data.success) {
            this.$refs.toast.success(this.isEditing ? 'Drug updated successfully' : 'Drug added successfully');
            bootstrap.Modal.getInstance(document.getElementById('drugModal')).hide();
            this.loadDrugs();
          } else {
            this.$refs.toast.error(res.data.message || 'Failed to save drug');
          }
        })
        .catch((e) => {
          this.saving = false;
          this.$refs.toast.error('Error saving drug');
        });
    },

    // View Drug
    viewDrug(drug) {
      this.selectedDrug = drug;
      const modal = new bootstrap.Modal(document.getElementById('viewDrugModal'));
      modal.show();
    }
  },
  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    this.loadDrugs();
  }
};
</script>

<style scoped>
.mini-stats-wid .card-body { padding: 20px; }
.mini-stat-icon { width: 48px; height: 48px; }
.avatar-sm { width: 3rem; height: 3rem; }
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
}

.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.15); }
.bg-danger-subtle { background-color: rgba(244, 106, 106, 0.15); }

.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  border: none;
}
.btn-soft-primary:hover { background-color: rgba(85, 110, 230, 0.2); }

.btn-soft-warning {
  background-color: rgba(241, 180, 76, 0.1);
  color: #f1b44c;
  border: none;
}
.btn-soft-warning:hover { background-color: rgba(241, 180, 76, 0.2); }

.table > :not(caption) > * > * { padding: 0.75rem; }
</style>
